import { GridRowId } from "@mui/x-data-grid-pro";
import React from "react";
import { AavoDataGridApi } from "src/components/common/dataGrid/AavoDataGridApi.ts";
import { GridInitialState } from "@mui/x-data-grid-pro";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import type { ControlledAsyncDataGridProps } from "src/components/common/dataGrid/ControlledAsyncDataGrid.tsx";
import { useIsMobile } from "src/utils/breakpointUtils.ts";
import { DataGridRowUpdateHandler } from "src/components/common/dataGrid/gridModel/dataGridRowUpdates.ts";
import type { AavoDataGridProps } from "src/components/common/dataGrid/AavoDataGrid.tsx";

export interface UseDataGridModelCommonParams<RowData extends object, TParams extends object> {
	gridId: string;
	initialParams: TParams;
	storedParams?: Array<keyof TParams>;
	getRowId: (row: RowData) => GridRowId;
	afterRefresh?: (params: DataGridModelAfterRefreshParams<RowData>) => void;
	apiRef?: React.RefObject<AavoDataGridApi<RowData> | null>;
	selectedRows?: RowData[];
	defaultSelectedRows?: RowData[];
	onSelectionChanged?: (rows: RowData[]) => void;
	defaultGridState?: GridInitialState;
	refreshRef?: RefreshableElementRef<TParams>;
	selectFirstRowOnLoad?: boolean;
	customFunctionUiActionView?: AavoDataGridProps<RowData>["customFunctionUiActionView"];
	customFunctionUiActionObjectId?: AavoDataGridProps<RowData>["customFunctionUiActionObjectId"];
}

export interface UseDataGridModelCommonReturn<RowData extends object, TParams> {
	getApi: () => AavoDataGridApi<RowData>;
	selectedRows: RowData[];
	onlySelectedRow: RowData | undefined;
	setSelectedRows: (rows: RowData[]) => void;
	currentParams: TParams;
	// Updates currents params and refreshes data
	refreshData: (params?: Partial<TParams>) => Promise<RowData[]>;
}

export type UseDataGridModelGridCommonProps<RowData extends object> = Required<
	Pick<
		ControlledAsyncDataGridProps<RowData>,
		| "rowsAsync"
		| "refreshData"
		| "initialState"
		| "onStateChange"
		| "selectedRows"
		| "onRowSelectionChanged"
		| "getRowId"
		| "actionBarMenuComponents2"
		| "apiRef"
		| "gridId"
	>
> & {
	updateRows?: DataGridRowUpdateHandler;
} & Pick<AavoDataGridProps<RowData>, "customFunctionUiActionView" | "customFunctionUiActionObjectId">;

export interface DataGridModelAfterRefreshParams<RowData extends object> {
	isFirstLoad: boolean;
	newRows: RowData[];
	setSelectedRows: (rows: RowData[]) => void;
}

export function usePersistentDataGridId(gridId: string): string {
	const isMobile = useIsMobile();
	return isMobile ? `${gridId}_mobile` : gridId;
}
